import React, { useState } from "react";
import {
  func,
  number,
  bool,
  string,
  instanceOf,
  oneOfType,
  object,
} from "prop-types";
import { Row, Col, Form, Dropdown, DropdownButton } from "react-bootstrap";
import Select from "react-select-plus";
import caretUpImage from "resources/images/caret-up.png";
import caretDownImage from "resources/images/caret-down.png";
import "react-select-plus/dist/react-select-plus.css";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import SetEventExpiration from "components/SetEventExpiration";
import SearchableDropdown from "components/SearchableDropdown";
import {
  getFormattedTimeZoneText,
  getEmailTemplateDropdownItems,
} from "utils/helperFunctions";
import { timeZoneList } from "containers/App/constants";
import {
  showSendReminder,
  getEventReminderDayOptions,
  getEventReminderDateOptions,
} from "./helpers";
import EmailPreviewBox from "./EmailPreviewBox";

const TestingEventExpiration = ({
  testingEventExpiration,
  eventExpirationDays,
  sendReminder,
  eventReminderDays,
  handleTestingEventExpirationToggle,
  handleEventExpirationDaysDropdown,
  handleEventReminderDaysDropdown,
  handleChangeSendReminder,
  isAU,
  selectedEventExpiration,
  handleSelectEventExpiration,
  eventExpirationDate,
  handleEventExpirationDateChange,
  eventExpirationTime,
  handleEventExpirationTime,
  showEventExpirationTimeError,
  eventExpirationTimeZone,
  handleTimezoneDropdown,
  handleEventReminderEmailTemplateDropdown,
  eventReminderEmailTemplateId,
  emailTemplates,
  isInsightUser,
  companyName,
  vhs,
  notifySMS,
}) => {
  const [viewEmailPreview, setViewEmailPreview] = useState(false);

  // set options for reminder day dropdown
  let eventReminderDayOptions = [];

  if (eventExpirationDays !== null) {
    eventReminderDayOptions = getEventReminderDayOptions(eventExpirationDays);
  } else if (eventExpirationDate !== null && eventExpirationTime !== null) {
    eventReminderDayOptions = getEventReminderDateOptions(
      eventExpirationDate,
      eventExpirationTime
    );
  }

  let currentTimezone = null;
  const timezoneDropdownItems = timeZoneList.map((timezoneItem) => {
    const { tzCode } = timezoneItem;

    const timezoneDropdownText = getFormattedTimeZoneText(
      tzCode,
      timeZoneList,
      isAU,
      eventExpirationTimeZone
    );

    const dropdownItem = {
      value: tzCode,
      label: timezoneDropdownText,
    };
    if (tzCode === eventExpirationTimeZone) {
      currentTimezone = dropdownItem;
    }
    return dropdownItem;
  });

  // show event expiration reminder if the new date is greater than one day after the current date
  const showEventExpirationReminder = showSendReminder(
    eventExpirationDays,
    eventExpirationDate,
    eventExpirationTime,
    1
  );

  // show send another reminder toggle if new date is greater than or equal to 3 days
  const showSendAnotherReminderToggle = showSendReminder(
    eventExpirationDays,
    eventExpirationDate,
    eventExpirationTime,
    3
  );

  const handleViewEmailPreview = (e) => {
    e.preventDefault();
    setViewEmailPreview(!viewEmailPreview);
  };

  let selectedEmailTemplate = null;
  if (
    Number(eventReminderEmailTemplateId) !== 0 &&
    emailTemplates &&
    emailTemplates.size > 0
  ) {
    selectedEmailTemplate = emailTemplates.get(
      String(eventReminderEmailTemplateId)
    );
  }

  const selectedEmailTemplateName =
    String(eventReminderEmailTemplateId) === "-6"
      ? "Default Reminder Email Template"
      : selectedEmailTemplate.get("emailName");

  const emailDropdownItems = getEmailTemplateDropdownItems(emailTemplates);

  return (
    <Row>
      <Col>
        <h4 className="mb-3">Event Expiration</h4>
        <Form>
          <div className="d-flex align-items-center mb-3">
            <div className="d-flex react-toggle-set-small align-items-center pt-0">
              <Toggle
                id="testEventExpirationToggle"
                className="global-toggle react-toggle-small"
                icons={false}
                checked={testingEventExpiration}
                onChange={handleTestingEventExpirationToggle}
              />
              <div className="d-inline-block">
                Set expiration on event(s) created via this invitation
              </div>
            </div>
          </div>
          {testingEventExpiration && (
            <>
              <SetEventExpiration
                selectedEventExpiration={selectedEventExpiration}
                setSelectedEventExpiration={handleSelectEventExpiration}
                eventExpirationDays={eventExpirationDays}
                handleTestExpirationDropdown={handleEventExpirationDaysDropdown}
                eventExpirationDate={eventExpirationDate}
                handleEventExpirationDateChange={
                  handleEventExpirationDateChange
                }
                eventExpirationTime={eventExpirationTime}
                setEventExpirationTime={handleEventExpirationTime}
                showEventExpirationTimeError={showEventExpirationTimeError}
                fromSchedulingEventExpiration
              />
              <Row className="mt-3 align-items-center">
                <Col>
                  <div style={{ fontWeight: "bold" }}>Timezone</div>
                  <SearchableDropdown
                    id="timezoneDropdownJobSettings"
                    options={timezoneDropdownItems}
                    onChange={handleTimezoneDropdown}
                    initialValue={currentTimezone}
                  />
                </Col>
              </Row>
              <Row className="px-3 mt-2 mb-0 align-items-center">
                <div>
                  <div style={{ fontWeight: "bold" }}>Reminder Email</div>
                  <DropdownButton
                    variant="outline-secondary"
                    onSelect={handleEventReminderEmailTemplateDropdown}
                    id="reminderEmailDropdown"
                    title={selectedEmailTemplateName}
                    disabled={vhs < 2}
                  >
                    <Dropdown.Item key="-6" eventKey="-6">
                      Default Reminder Email Template
                    </Dropdown.Item>
                    {emailDropdownItems}
                  </DropdownButton>
                </div>
                <div className="m-3 pt-3">
                  <button
                    type="button"
                    className="btn-as-link px-1"
                    style={{ color: "#425cc7" }}
                    onClick={handleViewEmailPreview}
                  >
                    Preview
                    <span>
                      {viewEmailPreview ? (
                        <img
                          src={caretUpImage}
                          height="16px"
                          alt="caret symbol facing up"
                          style={{ marginBottom: "2px" }}
                        />
                      ) : (
                        <img
                          src={caretDownImage}
                          height="16px"
                          alt="caret symbol facing down"
                          style={{ marginBottom: "2px" }}
                        />
                      )}
                    </span>
                  </button>
                </div>
              </Row>
              {viewEmailPreview && (
                <EmailPreviewBox
                  emailToPreview={selectedEmailTemplate}
                  isInsightUser={isInsightUser}
                  companyName={companyName}
                  defaultEmailTemplateType="eventExpirationReminder"
                />
              )}
              {showEventExpirationReminder ? (
                <div
                  className="mb-3"
                  style={{
                    color: "#53565a",
                    fontSize: "12px",
                  }}
                >
                  A reminder email {notifySMS ? "and SMS" : ""} will
                  automatically be sent 24 hours prior to expiration.
                </div>
              ) : null}
              {showSendAnotherReminderToggle ? (
                <div className="d-flex align-items-center mb-2">
                  <div className="d-flex react-toggle-set-small align-items-center pt-0">
                    <Toggle
                      id="testingEventSendReminder"
                      className="global-toggle react-toggle-small"
                      icons={false}
                      checked={sendReminder}
                      onChange={handleChangeSendReminder}
                    />
                    <div className="d-inline-block">Send another reminder</div>
                  </div>
                </div>
              ) : null}
              {showSendAnotherReminderToggle && sendReminder ? (
                <>
                  <div className="mb-1">Send reminder in</div>
                  <div className="mb-3" style={{ display: "flex" }}>
                    <Select
                      name="testingEventReminderDays"
                      className="eventReminderDaysSelect"
                      id="testingEventReminderDays"
                      value={eventReminderDays}
                      options={eventReminderDayOptions}
                      onChange={handleEventReminderDaysDropdown}
                      clearable={false}
                      style={{ width: "150px" }}
                      disabled={!testingEventExpiration}
                    />
                  </div>
                </>
              ) : null}
            </>
          )}
        </Form>
      </Col>
    </Row>
  );
};

TestingEventExpiration.propTypes = {
  handleTestingEventExpirationToggle: func,
  handleEventExpirationDaysDropdown: func,
  handleEventReminderDaysDropdown: func,
  testingEventExpiration: bool,
  eventExpirationDays: number,
  sendReminder: bool,
  eventReminderDays: number,
  handleChangeSendReminder: func,
  isAU: bool,
  selectedEventExpiration: string,
  handleSelectEventExpiration: func,
  eventExpirationDate: string,
  handleEventExpirationDateChange: func,
  eventExpirationTime: instanceOf(Date),
  handleEventExpirationTime: func,
  showEventExpirationTimeError: bool,
  eventExpirationTimeZone: string,
  handleTimezoneDropdown: func,
  handleEventReminderEmailTemplateDropdown: func,
  eventReminderEmailTemplateId: oneOfType([number, string]),
  emailTemplates: object,
  isInsightUser: bool,
  companyName: string,
  vhs: number,
  notifySMS: bool,
};

export default TestingEventExpiration;
